import { useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Database, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ConfirmDialog } from "./ConfirmDialog";
import { SettingsSection } from "./SettingsSection";

export function DataCard({ email }: { email: string }) {
  const { clear } = useAuth();
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/settings/export", { responseType: "blob" });
      const name = /filename="?([^"]+)"?/.exec(r.headers["content-disposition"] ?? "")?.[1] ?? "otium-mis-datos.xlsx";
      const url = URL.createObjectURL(r.data as Blob);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Descargamos tu archivo de Excel");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await api.post("/settings/delete-account", { email: typed });
      clear();
      navigate("/login", { replace: true });
      toast.success("Eliminamos tu cuenta y todos tus datos");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <SettingsSection id="datos" icon={Database} title="Mis datos" desc="Llévate tu información o elimina tu cuenta.">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-800">Descargar mis datos</p>
          <p className="text-xs text-slate-500">Excel con cuentas, movimientos, programados, categorías y presupuesto.</p>
        </div>
        <Button onClick={download} disabled={busy} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="settings-download-data-button">
          <Download className="mr-2 h-4 w-4" /> {busy ? "Preparando…" : "Descargar mis datos"}
        </Button>
      </div>
      <div className="mt-7 flex flex-col gap-3 rounded-xl border border-rose-200 bg-rose-50/50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-rose-700">Eliminar mi cuenta</p>
          <p className="text-xs text-rose-600/80">Borra todos tus datos de forma definitiva y cierra tu sesión.</p>
        </div>
        <ConfirmDialog testId="delete-account" title="¿Eliminar tu cuenta para siempre?" confirmLabel="Eliminar mi cuenta"
          description="Se borran tus cuentas, movimientos, programados, presupuesto y conversación. No se puede deshacer. Si tienes una suscripción activa, la cancelamos en Stripe en este mismo paso y ya no se te cobrará."
          onConfirm={remove} disabled={typed.trim().toLowerCase() !== email.toLowerCase()}
          trigger={<Button variant="outline" onClick={() => setTyped("")} className="h-11 border-rose-300 bg-white text-rose-600 hover:bg-rose-50" data-testid="settings-delete-account-button"><Trash2 className="mr-2 h-4 w-4" /> Eliminar mi cuenta</Button>}>
          <div className="space-y-1.5">
            <label htmlFor="delete-email" className="text-sm text-slate-700">Escribe <span className="font-semibold">{email}</span> para confirmar</label>
            <Input id="delete-email" value={typed} onChange={(e: ChangeEvent<HTMLInputElement>) => setTyped(e.target.value)} autoComplete="off" className="h-11" data-testid="delete-account-email-input" />
          </div>
        </ConfirmDialog>
      </div>
    </SettingsSection>
  );
}
